// src/components/PromoCTAButton.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchPublicPromotion } from "../api/api_functions";
import "./PromoCTAButton.css";

export default function PromoCTAButton() {
  const [promo, setPromo] = useState(null);
  const [serverNowISO, setServerNowISO] = useState(null);
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0, s: 0, done: true });
  const [copied, setCopied] = useState(false);

  const timerRef = useRef(null);
  const btnRef = useRef(null);

  // Posición del toast ("top" desktop / "bottom" móvil)
  const [place, setPlace] = useState("top");
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setPlace(mq.matches ? "bottom" : "top");
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  // Carga promoción + hora servidor
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { promotion, serverNow } = await fetchPublicPromotion();
        if (!mounted) return;
        setPromo(promotion || null);
        setServerNowISO(serverNow || null);
      } catch {
        if (!mounted) return;
        setPromo(null);
      }
    })();
    return () => {
      mounted = false;
      clearInterval(timerRef.current);
    };
  }, []);

  // Fin de promo
  const endAt = useMemo(() => {
    if (!promo?.endAtISO) return null;
    const d = new Date(promo.endAtISO);
    return Number.isNaN(d.getTime()) ? null : d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.endAtISO]);

  // Offset servidor-cliente
  const serverOffsetMs = useMemo(() => {
    if (!serverNowISO) return 0;
    const s = new Date(serverNowISO);
    return Number.isNaN(s.getTime()) ? 0 : s.getTime() - Date.now();
  }, [serverNowISO]);

  // Countdown
  // useEffect(() => {
  //   if (!endAt) return;
  //   const tick = () => {
  //     const now = Date.now() + serverOffsetMs;
  //     const diff = endAt.getTime() - now;
  //     if (diff <= 0) {
  //       setLeft({ d: 0, h: 0, m: 0, s: 0, done: true });
  //       return;
  //     }
  //     const d = Math.floor(diff / 86400000);
  //     const h = Math.floor((diff % 86400000) / 3600000);
  //     const m = Math.floor((diff % 3600000) / 60000);
  //     const s = Math.floor((diff % 60000) / 1000);
  //     setLeft({ d, h, m, s, done: false });
  //   };
  //   tick();
  //   timerRef.current = setInterval(tick, 1000);
  //   return () => clearInterval(timerRef.current);
  // }, [endAt, serverOffsetMs]);

  // Countdown (si la promo termina más allá de fin de mes, mostramos fin de mes)
  useEffect(() => {
    if (!endAt) return;

    const getEndOfCurrentMonth = (nowMs) => {
      const now = new Date(nowMs);
      // Último instante del mes actual (23:59:59.999)
      return new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
    };

    const tick = () => {
      const now = Date.now() + serverOffsetMs;

      // Objetivo “visible”: el mínimo entre (fin real de promo) y (fin de mes actual)
      const endOfMonth = getEndOfCurrentMonth(now);
      const targetMs = Math.min(endAt.getTime(), endOfMonth.getTime());

      const diff = targetMs - now;

      if (diff <= 0) {
        setLeft({ d: 0, h: 0, m: 0, s: 0, done: true });
        return;
      }

      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);

      setLeft({ d, h, m, s, done: false });
    };

    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [endAt, serverOffsetMs]);

  // Coordenadas del toast (si está visible)
  const [toastPos, setToastPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!copied || !btnRef.current) return;
    const calc = () => {
      const r = btnRef.current.getBoundingClientRect();
      const top =
        (place === "bottom" ? r.bottom + 12 : r.top - 12) + window.scrollY;
      const left = r.left + r.width / 2 + window.scrollX;
      setToastPos({ top, left });
    };
    calc();
    window.addEventListener("scroll", calc, { passive: true });
    window.addEventListener("resize", calc);
    return () => {
      window.removeEventListener("scroll", calc);
      window.removeEventListener("resize", calc);
    };
  }, [copied, place]);

  const fmt2 = (n) => String(n).padStart(2, "0");

  // Copiar al portapapeles
  const copyText = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch (e) {
      console.warn("No se pudo copiar al portapapeles:", e);
    }
  };

  const handleActivate = async () => {
    if (promo?.code) {
      await copyText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
      // setTimeout(() => { window.location.assign("/pricing"); }, 350);
    } else {
      // window.location.assign("/pricing");
    }
  };

  const isOpen = !!promo;
  const portalTarget =
    typeof document !== "undefined" ? document.body : null;

  // ⬇️ Render (sin hooks condicionales)
  return promo ? (
    <span className={`promo-cta-anchored ${isOpen ? "is-open" : ""}`}>
      <span className="promo-cta-collapser">
        <button
          ref={btnRef}
          type="button"
          onClick={handleActivate}
          className="btn-promo-cta"
          aria-label="Promoción activa"
        >
          {/* — estructura del botón — */}
          <span className="btn-left">
            <span className="btn-headlineRow">
              <span className="btn-col pct">
                <span className="btn-pct">{promo.headlinePct || ""}</span>
              </span>
              <span className="btn-col texts">
                <span className="btn-suffix">
                  {promo.headlineSuffix || ""}
                </span>
                {promo.condition && (
                  <span className="btn-cond">{promo.condition}</span>
                )}
              </span>
            </span>
          </span>

          <span className="btn-chip">
            <span className="btn-code-label">CÓDIGO</span>
            <span className="btn-code">{promo.code || ""}</span>
          </span>

          {endAt && !left.done ? (
            <span className="btn-countdown">
              {left.d}d {fmt2(left.h)}h {fmt2(left.m)}m {fmt2(left.s)}s
            </span>
          ) : null}
        </button>

        {/* sub flotante */}
        {promo.sub ? (
          <span className="btn-sub-floating" aria-live="polite">
            {promo.sub}
          </span>
        ) : null}

        {/* Toast en portal (por encima de Overview/MainSection) */}
        
      </span>
      {copied && portalTarget &&
          createPortal(
            <span
              className="btn-promo-toast btn-promo-toast--portal"
              role="status"
              aria-live="polite"
              style={{
                position: "fixed",
                top: `${toastPos.top}px`,
                left: `${toastPos.left}px`,
                transform: "translate(-50%, 0)",
                zIndex: 2147483647,
                background: "#111",
                color: "#fff",
                padding: "3px 10px",
                borderRadius: "8px",
                boxShadow: "0 8px 18px rgba(0,0,0,.25)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                opacity: 0.98,
              }}
            >
              Código copiado
            </span>,
            portalTarget
          )}
    </span>
    
  ) : null;
}
